import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDialogFocus } from 'hooks/useDialogFocus';
import { useOrientation } from 'hooks/useOrientation';
import { useSupportsHover } from 'hooks/useSupportsHover';

import ForcePortrait from 'components/ForcePortrait/ForcePortrait';

/**
 * BottomSheet — a mobile-native bottom drawer with:
 *   • swipe-down dismiss (drag past a fraction of the sheet's height, OR a fast
 *     downward flick),
 *   • tap-outside dismiss (backdrop click runs the same exit animation),
 *   • a backdrop TINT that fades in lockstep with the sheet moving down so the
 *     background page transitions smoothly back into view during the gesture
 *     (rather than the backdrop staying opaque until the sheet is off-screen),
 *   • safe-area-aware bottom padding, and
 *   • entrance / exit slide animations driven by CSS transitions.
 *
 * The chrome (22px top radius, dark surface, 38×5 grab handle) intentionally
 * mirrors the `sheet` variant of CardShell in
 * `components/RegistrationPrototype/d4Primitives.tsx` so every drawer in the
 * app reads as one modal system. Extracted from the tournament "about to
 * start" prompt on 2026-09-04; other in-flight modals (biometric prompt, etc.)
 * should adopt this component over time.
 *
 * IMPLEMENTATION NOTE — backdrop opacity: the tint is animated via
 * `background-color: rgba(0, 0, 0, α)` instead of the wrapper's `opacity`.
 * CSS `opacity` cascades to descendants, so an opacity-animated backdrop would
 * silently render the sheet inside it translucent — the lobby's bright orange
 * tiles behind would bleed through the sheet's own fill. The alpha-channel
 * approach fades only the wrapper's black tint, leaving the sheet fully opaque.
 */

type Props = {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /**
   * Optional: for this long after the sheet opens, a tap on the backdrop is ignored
   * instead of dismissing. A one-time sheet (the heads-up introduction, EN-10748) opens
   * under the finger that just tapped the lobby; without this a second tap of the same
   * gesture lands outside the sheet and burns its one showing. 0 (the default) keeps
   * every backdrop tap a dismissal.
   */
  backdropGuardMs?: number;
  /**
   * Optional: identifies the backdrop node for e2e tests. Defaults to
   * `bottom-sheet-backdrop`. Existing screens with their own testid can pass
   * theirs through until the tests catch up.
   */
  testId?: string;
  /**
   * Optional caps on the sheet width so it doesn't stretch edge-to-edge on
   * tablet-class widths. Defaults to 540px, matching the design.
   */
  maxWidth?: number | string;
  /**
   * Optional: rotate WITH the portrait-locked lobby on a phone held in landscape
   * (`ForcePortrait`). The portal then lives inside the rotated box, the swipe-down
   * gesture follows the rotated axis, and `--sheet-viewport-height` reports the rotated
   * height so content can size itself against it. Off by default.
   */
  forcePortrait?: boolean;
  /** Id of the element that names the dialog (its title), so the dialog has an accessible name. */
  ariaLabelledBy: string;
};

// Swipe-down dismiss thresholds — a drag past this fraction of the sheet's own
// height OR a fast enough downward flick both close the drawer.
const SWIPE_DISMISS_FRACTION = 0.28;
const SWIPE_DISMISS_VELOCITY_PX_PER_MS = 0.6;

// Exit animation duration, shared by the sheet slide-out and the backdrop
// fade so the two moves land on the same frame.
const EXIT_MS = 220;

// Backdrop tint alpha at rest. Fades to 0 during dismiss so the underlying
// page comes back into view smoothly. Animated via `background-color` — not
// `opacity` — see the IMPLEMENTATION NOTE above.
const BACKDROP_ALPHA = 0.6;

const BottomSheet: React.FC<Props> = ({
  open,
  onClose,
  children,
  backdropGuardMs = 0,
  testId = 'bottom-sheet-backdrop',
  maxWidth = 540,
  forcePortrait = false,
  ariaLabelledBy
}) => {
  const sheetRef = useRef<HTMLDivElement>(null);
  const supportsHover = useSupportsHover();
  const orientation = useOrientation();
  // Same condition ForcePortrait rotates on: a touch phone held in landscape.
  const rotated = forcePortrait && !supportsHover && orientation === 'landscape';
  // Inside the rotated box, the box's "down" is the screen's leftward axis.
  const pointerY = (touch: { clientX: number; clientY: number }) =>
    rotated ? -touch.clientX : touch.clientY;
  const escapeRef = useRef<() => void>(() => onClose());
  useDialogFocus(sheetRef, () => escapeRef.current(), open);
  const backdropRef = useRef<HTMLDivElement>(null);
  const dragStart = useRef<{ y: number; t: number } | null>(null);
  // Guard so a tap-outside during an exit animation doesn't fire onClose twice.
  const closing = useRef(false);
  // When this opening began, for backdropGuardMs.
  const openedAt = useRef(Date.now());
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (!open) return;
    closing.current = false;
    openedAt.current = Date.now();
    // Two-frame delay: first frame writes the initial off-screen transform,
    // second frame lets the CSS transition animate it to 0.
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setMounted(true));
    });
    return () => {
      window.cancelAnimationFrame(id);
      setMounted(false);
    };
  }, [open]);

  if (!open) return null;

  const setBackdropAlpha = (alpha: number, animate: boolean) => {
    if (!backdropRef.current) return;
    backdropRef.current.style.transition = animate
      ? `background-color ${EXIT_MS}ms ease-out`
      : 'none';
    backdropRef.current.style.backgroundColor = `rgba(0, 0, 0, ${alpha})`;
  };

  const applyDragTransform = (deltaY: number) => {
    const sheetHeight = sheetRef.current?.getBoundingClientRect().height ?? 0;
    if (sheetRef.current) {
      sheetRef.current.style.transition = 'none';
      sheetRef.current.style.transform = `translateY(${deltaY}px)`;
    }
    const progress = sheetHeight > 0 ? Math.min(1, deltaY / sheetHeight) : 0;
    setBackdropAlpha(BACKDROP_ALPHA * (1 - progress), false);
  };

  const snapBack = () => {
    if (sheetRef.current) {
      sheetRef.current.style.transition = 'transform 0.2s ease-out';
      sheetRef.current.style.transform = 'translateY(0)';
    }
    setBackdropAlpha(BACKDROP_ALPHA, true);
  };

  const animateExit = () => {
    if (closing.current) return;
    closing.current = true;
    const sheetHeight = sheetRef.current?.getBoundingClientRect().height ?? 400;
    if (sheetRef.current) {
      sheetRef.current.style.transition = `transform ${EXIT_MS}ms ease-in`;
      sheetRef.current.style.transform = `translateY(${sheetHeight}px)`;
    }
    setBackdropAlpha(0, true);
    window.setTimeout(onClose, EXIT_MS);
  };
  escapeRef.current = animateExit;

  // A backdrop tap dismisses, unless it lands inside the opening guard: then it is the
  // opening gesture's own echo and the sheet stays, without starting its exit.
  const onBackdropClick = () => {
    if (Date.now() - openedAt.current < backdropGuardMs) return;
    animateExit();
  };

  const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    const t = e.touches[0];
    dragStart.current = { y: pointerY(t), t: Date.now() };
  };
  const onTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!dragStart.current) return;
    const delta = pointerY(e.touches[0]) - dragStart.current.y;
    // Only track downward drag — upward drag on a bottom sheet should resist.
    applyDragTransform(Math.max(0, delta));
  };
  const onTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!dragStart.current) return;
    const endY = pointerY(e.changedTouches[0]);
    const delta = endY - dragStart.current.y;
    const dt = Math.max(1, Date.now() - dragStart.current.t);
    const velocity = delta / dt;
    dragStart.current = null;
    const sheetHeight = sheetRef.current?.getBoundingClientRect().height ?? 0;
    const shouldClose =
      delta > sheetHeight * SWIPE_DISMISS_FRACTION || velocity > SWIPE_DISMISS_VELOCITY_PX_PER_MS;
    if (shouldClose) {
      animateExit();
      return;
    }
    snapBack();
  };

  const modal = (
    <div
      ref={backdropRef}
      style={{
        ...backdropStyle,
        ['--sheet-viewport-height' as string]: rotated ? '100vw' : '100dvh',
        backgroundColor: `rgba(0, 0, 0, ${mounted ? BACKDROP_ALPHA : 0})`,
        transition: `background-color ${EXIT_MS}ms ease-out`
      }}
      // Tap OUTSIDE the sheet dismisses. Taps that started inside the sheet
      // stopPropagation below, so this handler only fires on real backdrop taps.
      onClick={onBackdropClick}
      data-testid={testId}>
      <div
        ref={sheetRef}
        role='dialog'
        aria-modal='true'
        aria-labelledby={ariaLabelledBy}
        tabIndex={-1}
        style={{
          ...sheetStyle,
          maxWidth,
          transform: mounted ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.28s cubic-bezier(0.22, 0.61, 0.36, 1)'
        }}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}>
        {/* Grab handle — 38×5 radius 3, matching the d4Primitives sheet. */}
        <div style={handleWrapStyle} aria-hidden>
          <span style={handleStyle} />
        </div>
        <div style={bodyStyle}>{children}</div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(
    forcePortrait ? <ForcePortrait>{modal}</ForcePortrait> : modal,
    document.body
  );
};

export default BottomSheet;

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const backdropStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  zIndex: 1300,
  // Backdrop-blur brings the sheet forward and quiets the busy lobby art
  // behind it — matches the biometric prompt's blurred stage. Alpha of the
  // tint is animated on background-color (see the IMPLEMENTATION NOTE at the
  // top); the blur is a static property that always applies while the sheet
  // is open, so it doesn't need to animate.
  backdropFilter: 'blur(14px) saturate(120%)',
  WebkitBackdropFilter: 'blur(14px) saturate(120%)'
};

const sheetStyle: CSSProperties = {
  width: '100%',
  // Solid #141e2a — the previous rgba(…, 0.98) let bright content bleed
  // through when the sheet sat over the lobby's warm-orange tiles.
  background: '#141e2a',
  borderTopLeftRadius: 22,
  borderTopRightRadius: 22,
  boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.6)',
  paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.5rem)',
  fontFamily: "'Outfit', sans-serif",
  color: 'rgba(255, 255, 255, 0.9)',
  touchAction: 'pan-y',
  outline: 'none'
};

const handleWrapStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  padding: '9px 0 2px',
  cursor: 'grab'
};

const handleStyle: CSSProperties = {
  width: 38,
  height: 5,
  borderRadius: 3,
  background: 'rgba(255, 255, 255, 0.28)'
};

// Vertical rhythm mirrors the biometric prompt: generous room ABOVE the lead
// icon and BELOW the last button, tight rhythm between the elements themselves
// (icon → title → body copy → actions). `gap` handles the between-element
// spacing; the padding gives the sheet its "sit-down room" on top and bottom.
const bodyStyle: CSSProperties = {
  padding: '2rem 1.5rem 2rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: '1rem'
};
